import {
  faEdit,
  faInfoCircle,
  faPlus,
  faSave,
  faSpinner
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Alert, Button, Card, Grid, Modal, Stack, Text, TextInput } from "@mantine/core";
import React, { useEffect, useState } from "react";
import ABAApi from "../../data/api/abaApi";
import { PersonBankDetails } from "../../types/aba";
import { validateABAField } from "../../util/abaValidation";
import { CenteredLoader } from "../common/loader";
import SensitivityWarning from "./SensitivityWarning";
import ValidationMessage from "./ValidationMessage";

interface PersonBankDetailsFormProps {
  /** Person ID for whom to manage bank details */
  personId: string;
  /** Association ID for scoping the bank details */
  associationId?: string;
  /** Person's name for display */
  personName: string;
  /** Existing bank details (if editing) */
  existingDetails?: PersonBankDetails | null;
  /** Whether this is shown as a modal */
  isModal?: boolean;
  /** Modal open state */
  isOpen?: boolean;
  /** Callback when modal is closed */
  onClose?: () => void;
  /** Callback when bank details are saved */
  onSave?: (details: PersonBankDetails) => void;
  /** Whether the user is editing their own details */
  isSelf?: boolean;
  /** Whether the form is disabled */
  disabled?: boolean;
}

interface BankDetailsFormData {
  bsb: string;
  accountNumber: string;
  accountName: string;
}

/**
 * Form component for creating/editing person bank details
 * Can be used as a modal or inline component
 */
const PersonBankDetailsForm: React.FunctionComponent<PersonBankDetailsFormProps> = ({
  personId,
  associationId,
  personName,
  existingDetails,
  isModal = false,
  isOpen = true,
  onClose,
  onSave,
  isSelf = false,
  disabled = false,
}) => {
  const [formData, setFormData] = useState<BankDetailsFormData>({
    bsb: "",
    accountNumber: "",
    accountName: "",
  });

  const [validation, setValidation] = useState<{ [key: string]: any }>({});
  const [loading, setLoading] = useState(false);
  const [validatingBSB, setValidatingBSB] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Initialize form data
  useEffect(() => {
    if (existingDetails) {
      setFormData({
        bsb: existingDetails.bsb,
        accountNumber: existingDetails.accountNumber,
        accountName: existingDetails.accountName,
      });
    } else {
      setFormData({
        bsb: "",
        accountNumber: "",
        accountName: "",
      });
    }
    setValidation({});
    setError(null);
    setSuccess(false);
  }, [existingDetails, isOpen]);

  const validateBSBFromBackend = async (bsb: string) => {
    const cleanedBSB = bsb.replace(/\D/g, '');

    if (cleanedBSB.length !== 6) {
      return;
    }

    setValidatingBSB(true);
    try {
      const result = await ABAApi.validateBSB(cleanedBSB);

      if (result.success && result.data?.valid) {
        const bsbInfo = result.data.bsbInfo;
        const bankName = bsbInfo?.bankName || 'Bank';
        const branchName = bsbInfo?.branchName;
        const displayName = branchName ? `${bankName} - ${branchName}` : bankName;

        setValidation(prev => ({
          ...prev,
          bsb: {
            isValid: true,
            formatted: `${cleanedBSB.slice(0, 3)}-${cleanedBSB.slice(3)}`,
            institutionName: displayName
          },
        }));
      } else {
        setValidation(prev => ({
          ...prev,
          bsb: {
            isValid: false,
            error: result.error || 'BSB is not valid or not found'
          },
        }));
      }
    } catch (error) {
      setValidation(prev => ({
        ...prev,
        bsb: {
          isValid: false,
          error: 'Failed to validate BSB. Please try again.'
        },
      }));
    } finally {
      setValidatingBSB(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    // Clear errors
    setError(null);
    setSuccess(false);

    // Handle BSB validation from backend
    if (name === 'bsb') {
      const cleanedBSB = value.replace(/\D/g, '');

      // Format BSB with dash as user types
      let formattedValue = value;
      if (cleanedBSB.length > 3) {
        formattedValue = `${cleanedBSB.slice(0, 3)}-${cleanedBSB.slice(3, 6)}`;
        setFormData(prev => ({ ...prev, [name]: formattedValue }));
      }

      // Basic client-side validation first
      if (cleanedBSB.length === 0) {
        setValidation(prev => ({
          ...prev,
          [name]: { isValid: false, error: 'BSB is required' },
        }));
      } else if (cleanedBSB.length < 6) {
        setValidation(prev => ({
          ...prev,
          [name]: { isValid: false, error: 'BSB must be 6 digits' },
        }));
      } else if (cleanedBSB.length === 6) {
        // Validate with backend when we have 6 digits
        validateBSBFromBackend(cleanedBSB);
      }
    } else {
      // For non-BSB fields, use client-side validation
      const fieldValidation = validateABAField(name as keyof BankDetailsFormData, value);
      setValidation(prev => ({
        ...prev,
        [name]: fieldValidation,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newValidation: { [key: string]: any } = {};
    let isValid = true;

    (Object.keys(formData) as (keyof BankDetailsFormData)[]).forEach((field) => {
      const fieldValidation = validateABAField(field, formData[field]);
      newValidation[field] = fieldValidation;
      if (!fieldValidation.isValid) {
        isValid = false;
      }
    });

    setValidation(newValidation);
    return isValid;
  };

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      // Use the appropriate API method based on whether we're editing or creating
      const effectiveAssociationId = associationId || '';
      if (!effectiveAssociationId) {
        throw new Error("Association ID is required for saving bank details");
      }

      const bankDetails = await ABAApi.savePersonBankDetails(personId, effectiveAssociationId, formData, isEditing, isSelf);
      setSuccess(true);

      if (onSave) {
        onSave(bankDetails);
      }

      // If this is a modal, close it after a brief delay
      if (isModal && onClose) {
        setTimeout(() => {
          onClose();
        }, 1500);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to save bank details");
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setFormData({
      bsb: "",
      accountNumber: "",
      accountName: "",
    });
    setValidation({});
    setError(null);
    setSuccess(false);
    if (onClose) {
      onClose();
    }
  };

  const isEditing = !!existingDetails;
  const formDisabled = disabled || loading;
  const canSubmit = !validatingBSB &&
    Object.values(validation).every(v => v?.isValid) &&
    Object.values(formData).every(v => v && typeof v === 'string' && v.trim() !== "");

  const formContent = (
    <div>
      {!isSelf && (
        <SensitivityWarning
          message={`You are ${isEditing ? 'editing' : 'adding'} bank details for ${personName}. This information is sensitive and will be encrypted.`}
        />
      )}

      {error && (
        <Alert color="red" className="mb-4">
          {error}
        </Alert>
      )}

      {success && (
        <Alert color="green" className="mb-4">
          <strong>Success!</strong> Bank details have been {isEditing ? 'updated' : 'saved'} successfully.
        </Alert>
      )}

      <form onSubmit={handleSubmit}>
        <Stack gap="md">
        <Grid>
          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="sm">
              <Text size="sm" fw={500} component="label" htmlFor="bsb">
                BSB <span className="text-danger">*</span>
                {validatingBSB && (
                  <span className="text-muted ms-2">
                    <FontAwesomeIcon icon={faSpinner} spin /> Validating...
                  </span>
                )}
              </Text>
              <div className="position-relative">
                <TextInput
                  type="text"
                  name="bsb"
                  id="bsb"
                  value={formData.bsb}
                  onChange={handleInputChange}
                  disabled={formDisabled || validatingBSB}
                  error={
                    validation.bsb && !validation.bsb.isValid
                      ? validation.bsb.error || "Invalid BSB"
                      : undefined
                  }
                  placeholder="062000 or 062-000"
                  maxLength={7}
                />
              </div>
              <ValidationMessage validation={validation.bsb} showWhenValid />
              <small className="text-muted">
                6-digit Bank State Branch number (validated with Australian banking directory)
              </small>
            </Stack>
          </Grid.Col>

          <Grid.Col span={{ base: 12, md: 6 }}>
            <Stack gap="sm">
              <Text size="sm" fw={500} component="label" htmlFor="accountNumber">
                Account Number <span className="text-danger">*</span>
              </Text>
              <TextInput
                type="text"
                name="accountNumber"
                id="accountNumber"
                value={formData.accountNumber}
                onChange={handleInputChange}
                disabled={formDisabled}
                error={
                  validation.accountNumber && !validation.accountNumber.isValid
                    ? validation.accountNumber.error || "Invalid account number"
                    : undefined
                }
                placeholder="123456789"
                maxLength={9}
              />
              <ValidationMessage validation={validation.accountNumber} />
              <small className="text-muted">
                Up to 9 digits
              </small>
            </Stack>
          </Grid.Col>
        </Grid>

        <Stack gap="sm">
          <Text size="sm" fw={500} component="label" htmlFor="accountName">
            Account Name <span className="text-danger">*</span>
          </Text>
          <TextInput
            type="text"
            name="accountName"
            id="accountName"
            value={formData.accountName}
            onChange={handleInputChange}
            disabled={formDisabled}
            error={
              validation.accountName && !validation.accountName.isValid
                ? validation.accountName.error || "Invalid account name"
                : undefined
            }
            placeholder="Account holder name"
            maxLength={32}
          />
          <ValidationMessage validation={validation.accountName} showWhenValid />
          <small className="text-muted">
            Account name as registered with the bank (up to 32 characters)
          </small>
        </Stack>

        {loading && <CenteredLoader />}

        {!isModal && (
          <div className="d-flex justify-content-end mt-4">
            <Button
              type="submit"
              color="blue"
              disabled={!canSubmit || formDisabled}
            >
              {loading ? (
                <>
                  <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                  Saving...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon={isEditing ? faSave : faPlus} className="me-2" />
                  {isEditing ? 'Update' : 'Add'} Bank Details
                </>
              )}
            </Button>
          </div>
        )}
        </Stack>
      </form>

      {!isModal && (
        <Alert color="blue" className="mt-4">
          <h6>
            <FontAwesomeIcon icon={faInfoCircle} className="me-2" />
            About Bank Details
          </h6>
          <ul className="mb-0">
            <li><strong>Security:</strong> Your bank details are encrypted and stored securely</li>
            <li><strong>Usage:</strong> These details are used for ABA (electronic) payments</li>
            <li><strong>Validation:</strong> Details are validated to ensure payments can be processed</li>
            <li><strong>Privacy:</strong> Only administrators and you can view these details</li>
          </ul>
        </Alert>
      )}
    </div>
  );

  if (isModal) {
    return (
      <Modal opened={isOpen} onClose={handleClose} size="lg" title={
        <>
          <FontAwesomeIcon icon={isEditing ? faEdit : faPlus} className="me-2" />
          {isEditing ? 'Edit' : 'Add'} Bank Details for {personName}
        </>
      }>
        {formContent}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1rem' }}>
          <Button color="gray" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="blue"
            onClick={() => handleSubmit()}
            disabled={!canSubmit || formDisabled}
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin className="me-2" />
                Saving...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={isEditing ? faSave : faPlus} className="me-2" />
                {isEditing ? 'Update' : 'Add'}
              </>
            )}
          </Button>
        </div>
      </Modal>
    );
  }

  return (
    <Card>
      <Card.Section withBorder p="md">
        <h5 className="mb-0">
          <FontAwesomeIcon icon={isEditing ? faEdit : faPlus} className="me-2" />
          {isEditing ? 'Edit' : 'Add'} Bank Details
          {!isSelf && ` for ${personName}`}
        </h5>
      </Card.Section>
      <div style={{ padding: "1rem" }}>
        {formContent}
      </div>
    </Card>
  );
};

export default PersonBankDetailsForm;
